/**
 * 같은 카테고리에 다른 사람들이 써 둔 이름.
 *
 * **치우는 것보다 안 생기게 하는 게 싸다.** 같은 물건을 `REALFORCE`와
 * `리얼포스`로 달리 적으면 랭킹에서 두 줄로 갈라지는데, 나중에 묶으려 들면
 * 끝이 없다. 쓰는 순간 "남들은 이렇게 적었다"를 보여주면 대부분 거기에 모인다.
 *
 * ⚠️ **목록으로 먼저 보여주지 않는다.** 카테고리 검색을 목록 없이 만든 이유와
 * 같다 — 목록을 보여주면 사람들은 좋아하는 것이 아니라 **보이는 것 중에서**
 * 고른다. 최애를 묻는 서비스에서 그건 질문 자체를 망친다. 그래서 두 글자
 * 이상 쳤을 때만, 이미 친 것을 **완성해 주는 형태로만** 나온다.
 *
 * 배치가 만든 정적 파일을 읽는다(`api: build:stats`). 파일이 없으면 조용히
 * 아무것도 제안하지 않는다 — 배치가 안 돌아도 픽을 남기는 데는 지장이 없다.
 *
 * 그 파일에는 **둘 이상이 쓴 이름만** 들어 있다. 누구나 통째로 내려받을 수
 * 있는 파일이라 한 사람만 쓴 글자는 싣지 않는다(`build-stats.ts` 참고).
 * 그래서 갓 생긴 이름은 한동안 제안에 안 뜬다 — 의도한 것이다.
 */

import { itemKeyOf } from '@lifetimeline/shared';

interface CategoryStats {
  categoryId: string;
  picks: number;
  items: { name: string; count: number }[];
}

interface StatsFile {
  generatedAt: string;
  categories: CategoryStats[];
}

const STATS_URL = '/data/stats/categories.json';
/** 이 글자 수부터 제안한다. 한 글자에 반응하면 사실상 목록을 펴는 것이다 */
export const SUGGEST_MIN_LENGTH = 2;

let pending: Promise<StatsFile | null> | null = null;

export async function loadStats(): Promise<StatsFile | null> {
  pending ??= fetch(STATS_URL)
    .then((response) => (response.ok ? (response.json() as Promise<StatsFile>) : null))
    .catch(() => null);
  return pending;
}

/** 인기순 카테고리 id. 배치 전이면 빈 배열 */
export function popularCategoryIds(stats: StatsFile | null): string[] {
  return (stats?.categories ?? []).map((category) => category.categoryId);
}

/**
 * 친 글자로 시작하거나 그것을 품은 이름들. 많이 쓰인 것이 앞이다.
 *
 * 똑같이 친 것은 빼고 준다 — 이미 친 것을 다시 제안해 봐야 누를 일이 없다.
 */
export function suggestItemNames(
  stats: StatsFile | null,
  categoryId: string,
  typed: string,
  limit = 5,
): string[] {
  const key = itemKeyOf(typed);
  if (stats === null || key.length < SUGGEST_MIN_LENGTH) {
    return [];
  }
  const items = stats.categories.find((c) => c.categoryId === categoryId)?.items ?? [];
  return (
    items
      .map((item) => ({ name: item.name, at: itemKeyOf(item.name).indexOf(key) }))
      .filter((match) => match.at >= 0 && itemKeyOf(match.name) !== key)
      // 앞에서 걸린 것이 먼저다. `리얼`로 쳤을 때 `리얼포스`가 `구형 리얼포스`보다 앞
      .sort((a, b) => a.at - b.at)
      .slice(0, limit)
      .map((match) => match.name)
  );
}
